<template>
  <iColumn
    :width="width"
    :height="height"
    :max-width="maxWidth"
    :min-width="minWidth"
    :max-height="maxHeight"
    :min-height="minHeight"
    gap="extraSmall"
  >
    <iVideoContainer
      v-if="isVideo"
      ref="videoContainer"
      :src="src"
      :poster="poster"
      controls
      :active="active"
      :muted="muted"
      :controls-style="controlsStyle"
      :width="width"
      :height="height"
      @play="$emit('play', $event)"
      @volumechange="$emit('volumechange', $event)"
    />
    <audio
      v-else-if="isAudio"
      ref="player"
      controls
      class="media-player__media"
    />
  </iColumn>
</template>

<script>
const HLS_JS_CDN_URL = "https://cdn.jsdelivr.net/npm/hls.js@1.5/dist/hls.min.js";
const HLS_MIME_TYPE = "application/vnd.apple.mpegurl";
const MEDIA_TYPE_VIDEO = "video";
const MEDIA_TYPE_AUDIO = "audio";

export default {
  name: "MediaPlayer",
  props: {
    src: {
      type: String,
      default: "",
    },
    isHls: {
      type: Boolean,
      default: false,
    },
    mediaType: {
      type: String,
      default: MEDIA_TYPE_VIDEO,
    },
    poster: {
      type: String,
      default: "",
    },
    active: {
      type: Boolean,
      default: null,
    },
    muted: {
      type: Boolean,
      default: false,
    },
    controlsStyle: {
      type: String,
      default: "standard",
    },
    width: {
      type: [String, Number],
      default: "hug",
    },
    height: {
      type: [String, Number],
      default: "hug",
    },
    maxWidth: {
      type: [String, Number],
      default: "unset",
    },
    minWidth: {
      type: [String, Number],
      default: "unset",
    },
    maxHeight: {
      type: [String, Number],
      default: "unset",
    },
    minHeight: {
      type: [String, Number],
      default: "unset",
    },
  },
  emits: ["play", "volumechange"],
  data() {
    return {
      hlsInstance: null,
    };
  },
  computed: {
    isVideo() {
      return this.mediaType === MEDIA_TYPE_VIDEO;
    },
    isAudio() {
      return this.mediaType === MEDIA_TYPE_AUDIO;
    },
  },
  watch: {
    src() {
      if (this.isAudio) {
        this.attachAudio();
      }
    },
  },
  mounted() {
    if (this.isAudio) {
      this.attachAudio();
    }
  },
  beforeUnmount() {
    this.destroyHls();
  },
  methods: {
    pauseVideo() {
      this.$refs.videoContainer?.pause();
    },
    attachAudio() {
      this.destroyHls();
      const player = this.$refs.player;
      if (!player || !this.src) {
        return;
      }
      if (!this.isHls || player.canPlayType(HLS_MIME_TYPE)) {
        player.src = this.src;
        return;
      }
      this.loadHlsJsScript()
          .then(() => this.attachHlsJs())
          .catch(() => {
            player.src = this.src;
          });
    },
    attachHlsJs() {
      const player = this.$refs.player;
      if (!player) {
        return;
      }
      if (!window.Hls || !window.Hls.isSupported()) {
        player.src = this.src;
        return;
      }
      this.hlsInstance = new window.Hls();
      this.hlsInstance.loadSource(this.src);
      this.hlsInstance.attachMedia(player);
    },
    loadHlsJsScript() {
      if (window.Hls) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = HLS_JS_CDN_URL;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("hls.js CDN load failed"));
        document.head.appendChild(script);
      });
    },
    destroyHls() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.media-player__media {
  width: 100%;
  display: block;
}
</style>
